import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Calendar, MapPin, Search, Filter } from 'lucide-react';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import ContactPreview from '../components/Home/ContactPreview';

const Events: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = [
    { id: 'all', name: 'All Events' },
    { id: 'it', name: 'IT & Technology' },
    { id: 'tourism', name: 'Tourism' },
    { id: 'agriculture', name: 'Agriculture' },
    { id: 'entertainment', name: 'Entertainment' },
    { id: 'corporate', name: 'Corporate' },
    { id: 'government', name: 'Government' }
  ];

  const events = [
    {
      id: 1,
      title: 'Euro PCR 2025',
      category: 'it',
      location: 'Vienna, Austria',
      date: 'March 2025',
      year: '2025',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?auto=format&fit=crop&w=600&q=80',
      description: 'Premier molecular diagnostics conference bringing together global experts in PCR technology and innovation.',
      status: 'upcoming'
    },
    {
      id: 2,
      title: 'Global Maritime India Summit 2024',
      category: 'government',
      location: 'Mumbai, India',
      date: 'October 2024',
      year: '2024',
      image: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=600&q=80',
      description: 'India\'s flagship maritime sector event focusing on sustainable shipping and port development.',
      status: 'completed'
    },
    {
      id: 3,
      title: 'International Film Festival of India (IFFI)',
      category: 'entertainment',
      location: 'Goa, India',
      date: 'November 2024',
      year: '2024',
      image: 'https://images.unsplash.com/photo-1489599904821-34f03d0c0837?auto=format&fit=crop&w=600&q=80',
      description: 'Prestigious international film festival showcasing cinema from around the world.',
      status: 'completed'
    },
    {
      id: 4,
      title: 'Aqua Goa - Fisheries & Aquaculture Expo',
      category: 'agriculture',
      location: 'Goa, India',
      date: 'September 2024',
      year: '2024',
      image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=600&q=80',
      description: 'Leading exhibition for fisheries, aquaculture, and allied industries in the Indian Ocean region.',
      status: 'completed'
    },
    {
      id: 5,
      title: 'Goa Tourism Investment Summit',
      category: 'tourism',
      location: 'Panaji, Goa',
      date: 'August 2024',
      year: '2024',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=600&q=80',
      description: 'Strategic summit to promote sustainable tourism development and investment opportunities in Goa.',
      status: 'completed'
    },
    {
      id: 6,
      title: 'Tech Innovation Conference 2025',
      category: 'it',
      location: 'Bengaluru, India',
      date: 'February 2025',
      year: '2025',
      image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=600&q=80',
      description: 'Cutting-edge technology conference featuring AI, blockchain, and emerging tech innovations.',
      status: 'upcoming'
    },
    {
      id: 7,
      title: 'Corporate Excellence Awards',
      category: 'corporate',
      location: 'Mumbai, India',
      date: 'December 2024',
      year: '2024',
      image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=600&q=80',
      description: 'Annual awards ceremony recognizing outstanding corporate performance and innovation.',
      status: 'upcoming'
    },
    {
      id: 8,
      title: 'Agricultural Innovation Summit',
      category: 'agriculture',
      location: 'Pune, India',
      date: 'January 2025',
      year: '2025',
      image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?auto=format&fit=crop&w=600&q=80',
      description: 'Summit focusing on sustainable farming practices and agricultural technology advancement.',
      status: 'upcoming'
    }
  ];

  const filteredEvents = events.filter(event => {
    const matchesCategory = activeCategory === 'all' || event.category === activeCategory;
    const matchesSearch = event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         event.location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      <Helmet>
        <title>Events | Mirchi Republic</title>
        <meta name="description" content="Explore our portfolio of events across IT, Tourism, Agriculture, Entertainment, and Corporate sectors. From international conferences to cultural festivals." />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-brand-1 to-brand-2 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-black/20" />
          <div className="container-grid relative z-10">
            <div className="col-span-12 text-center">
              <motion.h1
                className="text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                Our Events Portfolio
              </motion.h1>
              <motion.p
                className="text-xl lg:text-2xl opacity-90 max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                From international conferences to cultural celebrations, we create events that inspire, connect, and deliver exceptional results.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-12 bg-white dark:bg-dark-bg border-b border-neutral-200 dark:border-neutral-700">
          <div className="container-grid">
            <div className="col-span-12">
              {/* Search */}
              <div className="mb-8">
                <div className="relative max-w-md mx-auto lg:mx-0">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-brand-1 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Category Filters */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-3">
                {categories.map((category) => (
                  <motion.button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`chip transition-all duration-200 ${
                      activeCategory === category.id
                        ? 'bg-brand-1 text-white'
                        : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-brand-1/10'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {category.name}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Events Grid */}
        <section className="py-20 bg-neutral-50 dark:bg-neutral-900">
          <div className="container-grid">
            <div className="col-span-12">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredEvents.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card hover className="h-full overflow-hidden">
                      <div className="relative">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-48 object-cover"
                        />
                        <div className="absolute top-4 left-4">
                          <span className={`chip ${
                            event.status === 'upcoming' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-blue-500 text-white'
                          }`}>
                            {event.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                          </span>
                        </div>
                        <div className="absolute top-4 right-4">
                          <span className="chip bg-white/90 text-neutral-900">
                            {event.year}
                          </span>
                        </div>
                      </div>
                      
                      <div className="p-6 space-y-4">
                        <div>
                          <h3 className="text-xl font-semibold mb-2 line-clamp-2">
                            {event.title}
                          </h3>
                          <p className="text-neutral-600 dark:text-neutral-400 text-sm line-clamp-3">
                            {event.description}
                          </p>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center space-x-2 text-neutral-600 dark:text-neutral-400">
                            <Calendar className="w-4 h-4" />
                            <span>{event.date}</span>
                          </div>
                          <div className="flex items-center space-x-2 text-neutral-600 dark:text-neutral-400">
                            <MapPin className="w-4 h-4" />
                            <span>{event.location}</span>
                          </div>
                        </div>
                        
                        <div className="pt-4">
                          <Button variant="secondary" size="sm" fullWidth>
                            View Details
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
              
              {filteredEvents.length === 0 && (
                <div className="text-center py-12">
                  <Filter className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-neutral-600 dark:text-neutral-400 mb-2">
                    No events found
                  </h3>
                  <p className="text-neutral-500 dark:text-neutral-500">
                    Try adjusting your search or filter criteria
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        <ContactPreview />
      </div>
    </>
  );
};

export default Events;